import { AbstractDialogContent } from '@abstracts/dialog/dialog-content';
import { Component, Injector, OnInit, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { InvoiceSettingsDto } from '@dto/invoiceSettingsDto';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InvoicesService } from '@services/entity/invoices.service';
import { ToastService } from '@services/toast/toast.service';
import { plainToClass } from 'class-transformer';
import { NgxSpinnerService } from 'ngx-spinner';
import { UploadResponse } from '@dto/uploadResponse';
import { FilesService } from '@services/files/files.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { InvoiceDto } from '@dto/invoiceDto';
import { Transaction } from '@models/transaction';
import { UsersService } from '@services/entity/users.service';
import { UserDto } from '@dto/userDto';
import { FormService } from '@services/form/form.service';
import { ValidatorService } from 'angular-iban';
import { ActivatedRoute } from '@angular/router';
import { CustomersService } from '@services/entity/customers.service';

@Component({
  selector: 'app-edit-invoice-settings-dialog-content',
  templateUrl: './edit-invoice-settings-dialog-content.component.html',
  styleUrls: ['./edit-invoice-settings-dialog-content.component.scss']
})
export class EditInvoiceSettingsDialogContentComponent extends AbstractDialogContent implements OnInit {

  public className = 'EditInvoiceSettingsDialogContentComponent';

  public ownerUserId: string
  public user: UserDto
  public invoiceSettings: InvoiceSettingsDto = new InvoiceSettingsDto()
  public invoiceExample: InvoiceDto

  @Input() private customerId: number;

  public isUser: boolean = false
  public isCustomer: boolean = false
  public isOperator: boolean = false
  public isSupporter: boolean = false;
  
  public form: UntypedFormGroup
  public formReady: boolean = false

  public isHeaderHighlighted: boolean = false
  public isFooterHighlighted: boolean = false

  private file: File
  public filePreview: SafeUrl
  public customStationary: any

  constructor(
    public activeModal: NgbActiveModal,
    public dialog: NgbModal,
    private spinnerService: NgxSpinnerService,
    private toastService: ToastService,
    private filesService: FilesService,
    public formService: FormService,
    public activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    private customersService: CustomersService,
    private sanitizer: DomSanitizer,
    private invoicesService: InvoicesService,
    injector: Injector
  ) {
    super(activeModal, injector);
  }

  async ngOnInit() {
    super.ngOnInit();
    this.getRoles();

    if (this.isOperator) {
      this.customersService.getMasterUserOfCustomer(this.customerId).subscribe(masterUser => {
        this.ownerUserId = masterUser.id;
        this.getUser();
      })
    } else {
      this.ownerUserId = localStorage.getItem('userId');
      this.getUser();
    }
  }

  getUser() {
    this.usersService.getUserDtoById(this.ownerUserId).subscribe(user => {
      this.user = plainToClass(UserDto, user)
      this.getInvoiceSettings()
    })
  }

  getInvoiceSettings() {
    this.invoicesService.getInvoiceSettingsOfUser(this.ownerUserId).subscribe(invoiceSettings => {
      if (invoiceSettings) {
        this.invoiceSettings = plainToClass(InvoiceSettingsDto, invoiceSettings)
        this.getCustomStationary()
      } else if (!this.isSupporter){
        const newInvoiceSettings = new InvoiceSettingsDto()
        newInvoiceSettings.ownerUserId = this.user.id
        newInvoiceSettings.letterheadHeight = 70
        newInvoiceSettings.footnoteHeight = 20
        this.invoicesService.createInvoiceSettings(newInvoiceSettings).subscribe(createdInvoiceSettings => {
          this.invoiceSettings = plainToClass(InvoiceSettingsDto, createdInvoiceSettings)
        })
      }
      this.createForm()
    }, error => {
      console.error(error);
    })
  }

  getCustomStationary() {
    if (this.invoiceSettings.stationaryFileName) {
      this.filesService.getPublicImage(this.invoiceSettings.stationaryFileName, "image/jpg").subscribe(result => {
        var unsafeImageUrl = URL.createObjectURL(result);
        this.customStationary = this.sanitizer.bypassSecurityTrustUrl(unsafeImageUrl);
      }, error => {
        console.error(error);
      })
    }
  }

  createForm() {
    this.formService.validators = {
      ceo: [],
      vatId: [Validators.required],
      commercialRegister: [],
      commercialRegisterId: [],
      bank: [],
      iban: [Validators.minLength(22), Validators.maxLength(27), ValidatorService.validateIban],
      bic: [],
    }
    
    this.formService.form = new UntypedFormGroup({
      vatId: new UntypedFormControl(this.invoiceSettings?.vatId, this.formService.validators['vatId']),
      ceo: new UntypedFormControl(this.invoiceSettings?.ceo, this.formService.validators['ceo']),
      commercialRegister: new UntypedFormControl(this.invoiceSettings?.commercialRegister, this.formService.validators['commercialRegister']),
      commercialRegisterId: new UntypedFormControl(this.invoiceSettings?.commercialRegisterId, this.formService.validators['commercialRegisterId']),
      bank: new UntypedFormControl(this.invoiceSettings?.bank, this.formService.validators['bank']),
      iban: new UntypedFormControl(this.invoiceSettings?.iban, this.formService.validators['bank']),
      bic: new UntypedFormControl(this.invoiceSettings?.bic, this.formService.validators['bank']),
      isUsingStationery: new UntypedFormControl(this.invoiceSettings?.isUsingStationery),
      letterheadHeight: new UntypedFormControl(this.invoiceSettings?.letterheadHeight ? this.invoiceSettings?.letterheadHeight : 70),
      footnoteHeight: new UntypedFormControl(this.invoiceSettings?.footnoteHeight ? this.invoiceSettings?.footnoteHeight : 22),
      isUsingFootnote: new UntypedFormControl(this.invoiceSettings?.isUsingFootnote),
    });

    window.setTimeout(() => {
      document.getElementById('vatId').focus()
    }, 100)

    this.formService.formMap = new Map<string, UntypedFormGroup>();
    this.formReady = true;
    this.refresh();  
  }

  async setCustomStationary(event: any) {
    if (event.target.files[0] == null) return;
    this.file = new File([event.target.files[0]], this.ownerUserId + '.jpg', { type: 'image/jpg' });
    this.filePreview = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(event.target.files[0]))
  }

  async uploadFile(file: File): Promise<UploadResponse[]> {
    var formData = new FormData()
    formData.append("file", file)
    formData.append("path", "stationaries")
    return await this.filesService.uploadAsync(formData, true)
  }

  async validateIBAN() {
    this.invoicesService.validateIBAN(this.formService.form.value.iban).subscribe(result => {
      if (result && result.bankData) {
        this.invoiceSettings.bank = result.bankData.name
        this.formService.form.patchValue({bank: this.invoiceSettings.bank})
        this.invoiceSettings.bic = result.bankData.bic
        this.formService.form.patchValue({bic: this.invoiceSettings.bic})
        this.refresh()
      }
    })
  }

  async patchAndClose() {
    if (this.formService.form.invalid) {
      this.toastService.error('Bitte füllen Sie alle Pflichtfelder aus.')
      return false
    }

    this.spinnerService.show();

    this.invoiceSettings.ceo = this.formService.form.value.ceo
    this.invoiceSettings.vatId = this.formService.form.value.vatId
    this.invoiceSettings.commercialRegister = this.formService.form.value.commercialRegister
    this.invoiceSettings.commercialRegisterId = this.formService.form.value.commercialRegisterId
    this.invoiceSettings.bank = this.formService.form.value.bank
    this.invoiceSettings.iban = this.formService.form.value.iban
    this.invoiceSettings.bic = this.formService.form.value.bic
    this.invoiceSettings.isUsingStationery = this.formService.form.value.isUsingStationery
    this.invoiceSettings.letterheadHeight = this.formService.form.value.letterheadHeight
    this.invoiceSettings.footnoteHeight = this.formService.form.value.footnoteHeight
    this.invoiceSettings.isUsingFootnote = this.formService.form.value.isUsingFootnote

    if (this.file != null) {
      const fileUpload = await this.uploadFile(this.file)
      this.invoiceSettings.stationaryFileUri = fileUpload[0].fileUri
      this.invoiceSettings.stationaryFileName = fileUpload[0].fileName
    }

    this.invoicesService.patchInvoiceSettings(this.invoiceSettings).subscribe(result => {
      this.spinnerService.hide()
      this.toastService.success(`Änderungen erfolgreich gespeichert.`)
      this.activeModal.close()
    }, error => {
      this.spinnerService.hide()
      this.toastService.error(`Änderungen konnten nicht gespeichert werden.`)
      this.activeModal.close();
    });
  }

  public getStopDate(date, hours) {
    var result = new Date(date)
    result.setDate(result.getHours() + hours)
    return result
  }

  public getDueDate(date, days) {
    var result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
  }

  public highlightHeader(bool) {
    this.isHeaderHighlighted = bool;
  }
  
  public highlightFooter(bool) {
    this.isFooterHighlighted = bool;
  }

  close() {
    if (!this.invoiceSettings || !this.isInvoiceSettingsValid(this.invoiceSettings)) {
      this.activeModal.close(true);
    }
    this.activeModal.close();
  }

  private isInvoiceSettingsValid(settings) {
    if (settings.vatId) {
      return true
    }
    return false
  }
  
  refresh() {
    if (!this.invoiceSettings) return
    this.invoiceSettings.ceo = this.formService.form.value.ceo
    this.invoiceSettings.vatId = this.formService.form.value.vatId
    this.invoiceSettings.commercialRegister = this.formService.form.value.commercialRegister
    this.invoiceSettings.commercialRegisterId = this.formService.form.value.commercialRegisterId
    this.invoiceSettings.bank = this.formService.form.value.bank
    this.invoiceSettings.iban = this.formService.form.value.iban
    this.invoiceSettings.bic = this.formService.form.value.bic
    this.invoiceSettings.isUsingStationery = this.formService.form.value.isUsingStationery
    this.invoiceSettings.letterheadHeight = this.formService.form.value.letterheadHeight
    this.invoiceSettings.footnoteHeight = this.formService.form.value.footnoteHeight
    this.invoiceSettings.isUsingFootnote = this.formService.form.value.isUsingFootnote

    // Fixes the detection of ngOnChanges in invoice-pdf.components.ts
    this.invoiceExample = Object.assign({}, this.invoiceExample);
  }

  moveToAccount() {
    this.router.navigate(['../manage/profile'], { relativeTo: this.activatedRoute }).then(() => {
      this.activeModal.close()
    })
  }
  
  getRoles() {
    if (this.authenticationService.userHasRole('Operator')) this.isOperator = true;
    if (this.authenticationService.userHasRole('Customer')) this.isCustomer = true;
    if (this.authenticationService.userHasRole('User')) this.isUser = true;
    this.isSupporter = this.authenticationService.userHasRole('Supporter')
  }

  async downloadPreview() {
    this.toastService.loading('Beispielrechnung wird heruntergeladen...')
    
    const previewSettings : InvoiceSettingsDto = new InvoiceSettingsDto()

    previewSettings.ceo = this.formService.form.value.ceo
    previewSettings.vatId = this.formService.form.value.vatId
    previewSettings.commercialRegister = this.formService.form.value.commercialRegister
    previewSettings.commercialRegisterId = this.formService.form.value.commercialRegisterId
    previewSettings.bank = this.formService.form.value.bank
    previewSettings.iban = this.formService.form.value.iban
    previewSettings.bic = this.formService.form.value.bic
    previewSettings.isUsingStationery = this.formService.form.value.isUsingStationery
    previewSettings.letterheadHeight = this.formService.form.value.letterheadHeight
    previewSettings.footnoteHeight = this.formService.form.value.footnoteHeight
    previewSettings.isUsingFootnote = this.formService.form.value.isUsingFootnote

    if (previewSettings.isUsingStationery && this.file != null) {
      const previewFile = new File([this.file], this.ownerUserId + '-preview.jpg', { type: 'image/jpg' });
      const fileUpload = await this.uploadFile(previewFile)
      previewSettings.stationaryFileUri = fileUpload[0].fileUri
      previewSettings.stationaryFileName = fileUpload[0].fileName
    }

    this.invoicesService.downloadPreview(this.ownerUserId, previewSettings).subscribe((response: any) => {
      const file = new Blob([response], { type: 'application/pdf' });
      const fileUrl = URL.createObjectURL(file);
      const fileName = `Beispielrechnung.pdf`

      var a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = fileUrl;
      a.download = fileName;
      a.click();

      window.URL.revokeObjectURL(fileUrl);
      this.toastService.success('Beispielrechnung erfolgreich heruntergeladen.', 0, true)
    },
    error => {
      console.error(error);
      this.toastService.error(`Beispielrechnung konnte nicht heruntergeladen werden.`, 0, true)
    })
  }
}
